import { useEffect, useState } from 'react';
import { Instagram, Facebook } from 'lucide-react';
import logoHabitus from '@/assets/logo-habitus.png?format=webp';
import { CLINIC_INFO, QUICK_LINKS, LEGAL_LINKS } from '@/data/constants';
import { TREATMENT_LINKS } from '@/data/navigation';
import { abrirPreferenciasDeCookies } from '@/lib/consentimento';

// Avaliado uma vez, no modulo: no HTML estatico e o ano do build, e o cliente
// renderiza o mesmo valor na hidratacao (sem mismatch). O efeito abaixo corrige
// na virada do ano, para o rodape nao ficar desatualizado caso o site passe
// meses sem novo deploy.
const anoDoBuild = new Date().getFullYear();

const Footer = () => {
  const [currentYear, setCurrentYear] = useState(anoDoBuild);

  useEffect(() => {
    const agora = new Date().getFullYear();
    if (agora !== anoDoBuild) setCurrentYear(agora);
  }, []);

  return (
    <footer className="bg-[#1a1a1a] text-[#f5f5f0] border-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div>
            <a href="/">
              <img 
                src={logoHabitus} 
                alt="Habitus Saúde Integrativa" 
                width={646}
                height={162}
                className="h-12 w-auto mb-4 brightness-0 invert"
              />
            </a>
            <p className="text-[#f5f5f0]/70 text-sm leading-relaxed mb-4">
              Há mais de 10 anos cuidando de pessoas com medicina integrativa 
              e cuidado humanizado.
            </p>
            {/* Social Links */}
            <div className="flex gap-4">
              <a
                href={CLINIC_INFO.socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#f5f5f0]/10 flex items-center justify-center transition-colors hover:bg-primary"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5 text-[#f5f5f0]" />
              </a>
              <a
                href={CLINIC_INFO.socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#f5f5f0]/10 flex items-center justify-center transition-colors hover:bg-primary"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5 text-[#f5f5f0]" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4 text-[#f5f5f0]">Links Rápidos</h4>
            <ul className="space-y-2">
              {QUICK_LINKS.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-[#f5f5f0]/70 text-sm transition-colors hover:text-primary"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Tratamentos.
              Existe por SEO tanto quanto por navegacao: ate 30/07 estas 11
              paginas nao recebiam UM link interno no site inteiro, porque o
              unico caminho ate elas era o dropdown do cabecalho, que so
              aparecia depois do clique. O rodape esta em todas as 56 paginas e
              resolve isso de uma vez. Ver docs/SEO-GEO.md. */}
          <div>
            <h4 className="font-semibold mb-4 text-[#f5f5f0]">Tratamentos</h4>
            <ul className="space-y-2">
              {TREATMENT_LINKS.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-[#f5f5f0]/70 text-sm transition-colors hover:text-primary"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-4 text-[#f5f5f0]">Contato</h4>
            <ul className="space-y-2 text-[#f5f5f0]/70 text-sm">
              <li>
                <a 
                  href={`tel:+${CLINIC_INFO.callPhoneRaw}`}
                  className="transition-colors hover:text-primary"
                >
                  {CLINIC_INFO.callPhone}
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${CLINIC_INFO.email}`}
                  className="transition-colors hover:text-primary"
                >
                  {CLINIC_INFO.email}
                </a>
              </li>
              <li className="leading-relaxed">
                {CLINIC_INFO.address.street}<br />
                {CLINIC_INFO.address.complement}<br />
                {CLINIC_INFO.address.city}, {CLINIC_INFO.address.zipCode}
              </li>
            </ul>
          </div>
        </div>

        {/*
          Identificação obrigatória de estabelecimento de saúde (Resolução CFM
          2.336/2023, art. 5º e 6º). Fica no rodapé de propósito: é o único
          bloco presente em todas as páginas, então vale como "página
          principal" para qualquer rota de entrada. Não remover nem esconder.
        */}
        <div className="pt-8 border-t border-[#f5f5f0]/10 text-center md:text-left">
          <p className="text-[#f5f5f0]/70 text-sm leading-relaxed">
            {CLINIC_INFO.name} · Registro {CLINIC_INFO.registroCrm}
            <span className="hidden sm:inline"> · </span>
            <br className="sm:hidden" />
            Diretor Técnico-Médico: {CLINIC_INFO.diretorTecnico.nome} · MÉDICO · {CLINIC_INFO.diretorTecnico.crm}
          </p>
        </div>

        {/* Bottom */}
        <div className="pt-6 mt-6 border-t border-[#f5f5f0]/10 flex flex-col items-center gap-4 md:flex-row md:justify-between">
          <p className="text-[#f5f5f0]/50 text-sm">
            © {currentYear} {CLINIC_INFO.name}. Todos os direitos reservados.
          </p>
          <div className="flex flex-wrap gap-4 text-sm items-center">
            {LEGAL_LINKS.map((link) => (
              <a 
                key={link.href}
                href={link.href} 
                className="text-[#f5f5f0]/50 transition-colors hover:text-primary"
              >
                {link.label}
              </a>
            ))}
            <button
              type="button"
              onClick={abrirPreferenciasDeCookies}
              className="text-[#f5f5f0]/50 transition-colors hover:text-primary"
            >
              Preferências de cookies
            </button>
            <span className="text-[#f5f5f0]/50 hidden md:inline">|</span>
            <a
              href="/admin/login"
              className="text-[#f5f5f0]/60 text-xs transition-colors hover:text-primary"
            >
              Admin
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
